package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.registrierung.TrustedDevice
import org.example.project.registrierung.TrustedDevicesApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.w3c.dom.events.MouseEvent

@Composable
internal fun DashboardSidebar(
    categories: Map<String, List<All>>,
    categoryIcons: Map<String, String>,
    sidebarScreens: List<All>,
    popupScreens: List<All>,
    openTopbarScreens: List<String>,
    sidebarMode: String,
    sidebarWidthPx: Int,
    expandedSidebarWidthPx: Int,
    sidebarCollapsed: Boolean,
    sidebarAutoHide: Boolean,
    isSidebarVisible: Boolean,
    selectedKey: String,
    activeCompanyLabel: String,
    currentUser: String,
    onSidebarCollapsedChange: (Boolean) -> Unit,
    monitorAvailable: Boolean,
    monitorEnabled: Boolean,
    feedDefinitionsWithStatus: List<Pair<DashboardFeedDefinition, Boolean>>,
    userFeedPushEnabled: Map<String, Boolean>,
    onSidebarAutoHideChange: (Boolean) -> Unit,
    topbarAutoHide: Boolean,
    onTopbarAutoHideChange: (Boolean) -> Unit,
    onMonitorEnabledChange: (Boolean) -> Unit,
    onFeedPushEnabledChange: (String, Boolean) -> Unit,
    onToggleTopbarScreen: (String, Boolean) -> Unit,
    onSelect: (String) -> Unit,
    onHome: () -> Unit,
    onLogout: () -> Unit,
    onOpenCompany: (() -> Unit)?,
    onOpenProfile: (() -> Unit)?,
    onOpenProfilePopup: (() -> Unit)?,
    onPin: (String) -> Unit,
    onMouseLeave: () -> Unit
) {
    val expanded = remember { mutableStateMapOf<String, Boolean>() }
    var hoverCategory by remember { mutableStateOf<String?>(null) }
    var hoverCategoryTopPx by remember { mutableStateOf(0.0) }
    var hoverFlatScreen by remember { mutableStateOf<All?>(null) }
    var hoverFlatTopPx by remember { mutableStateOf(0.0) }
    var hoverIdentityLabel by remember { mutableStateOf<String?>(null) }
    var hoverIdentityTopPx by remember { mutableStateOf(0.0) }
    var hoverPopupLabel by remember { mutableStateOf<String?>(null) }
    var hoverPopupLeftPx by remember { mutableStateOf(0.0) }
    var hoverPopupTopPx by remember { mutableStateOf(0.0) }
    var popupGridVisible by remember { mutableStateOf(false) }
    var popupGridTopPx by remember { mutableStateOf(0.0) }

    fun showPopupTooltip(event: MouseEvent?, label: String) {
        hoverPopupLabel = label
        hoverPopupLeftPx = (event?.clientX?.toDouble() ?: 0.0) + 10.0
        hoverPopupTopPx = (event?.clientY?.toDouble() ?: 0.0) + 14.0
    }

    LaunchedEffect(categories.keys.toList()) {
        val validKeys = categories.keys.toSet()
        expanded.keys.toList()
            .filterNot { it in validKeys }
            .forEach { expanded.remove(it) }
    }

    Div({
        classes(DashboardStyles.sidebarWrapper)
        style {
            width(sidebarWidthPx.px)
            left(if (sidebarAutoHide && !isSidebarVisible) (-sidebarWidthPx).px else 0.px)
        }
        onMouseLeave {
            hoverCategory = null
            hoverFlatScreen = null
            hoverIdentityLabel = null
            hoverPopupLabel = null
            popupGridVisible = false
            onMouseLeave()
        }
    }) {
        Div({ classes(DashboardStyles.sidebar) }) {
            if (popupScreens.isNotEmpty()) {
                Div({
                    classes(DashboardStyles.sidebarPopupSection)
                    if (sidebarCollapsed) classes(DashboardStyles.sidebarPopupSectionCollapsed)
                }) {
                    if (sidebarCollapsed) {
                        Div({
                            classes(DashboardStyles.sidebarPopupIconButton)
                            onMouseEnter {
                                val event = it.nativeEvent as? MouseEvent
                                showPopupTooltip(event, "Popups")
                                popupGridTopPx = event?.currentTarget.asDynamic().getBoundingClientRect().top as? Double
                                    ?: event?.clientY?.toDouble()
                                    ?: 0.0
                                popupGridVisible = true
                                hoverCategory = null
                                hoverFlatScreen = null
                            }
                            onMouseMove {
                                showPopupTooltip(it.nativeEvent as? MouseEvent, "Popups")
                            }
                            onMouseLeave {
                                hoverPopupLabel = null
                            }
                        }) {
                            Img("images/apps.svg") {
                                classes(DashboardStyles.sidebarPopupIcon)
                            }
                        }
                    } else {
                        popupScreens.forEach { screen ->
                            val active = screen.key in openTopbarScreens
                            Div({
                                classes(DashboardStyles.sidebarPopupIconButton)
                                if (active) classes(DashboardStyles.sidebarPopupIconButtonActive)
                                onMouseEnter {
                                    showPopupTooltip(it.nativeEvent as? MouseEvent, screen.label.ifBlank { screen.key })
                                }
                                onMouseMove {
                                    showPopupTooltip(it.nativeEvent as? MouseEvent, screen.label.ifBlank { screen.key })
                                }
                                onMouseLeave {
                                    hoverPopupLabel = null
                                }
                                onClick { onToggleTopbarScreen(screen.key, active) }
                            }) {
                                Img(screen.icon.ifBlank { "images/step.svg" }) {
                                    classes(DashboardStyles.sidebarPopupIcon)
                                }
                            }
                        }
                    }
                }
                Div({ classes(DashboardStyles.sidebarPopupDivider) })
            }

            if (sidebarMode == SidebarMode.CATEGORIZED) {
                Div({ classes(DashboardStyles.navScroll) }) {
                    Div({ classes(DashboardStyles.navScrollContent) }) {
                        categories.forEach { (title, screens) ->
                            val isOpen = expanded[title] == true

                            Div({
                                classes(DashboardStyles.categoryRow)
                                if (sidebarCollapsed) classes(DashboardStyles.categoryRowCollapsed)
                                onMouseEnter {
                                    val event = it.nativeEvent as? MouseEvent
                                    hoverCategoryTopPx = event?.currentTarget.asDynamic().getBoundingClientRect().top as? Double
                                        ?: event?.clientY?.toDouble()
                                        ?: 0.0
                                    hoverCategory = title
                                    hoverFlatScreen = null
                                }
                                onClick {
                                    if (sidebarCollapsed) {
                                        hoverCategory = title
                                    } else {
                                        expanded.keys.toList().forEach { key ->
                                            expanded[key] = false
                                        }
                                        expanded[title] = !isOpen
                                    }
                                }
                            }) {
                                categoryIcons[title]?.let { icon ->
                                    Img(icon) { classes(DashboardStyles.categoryIcon) }
                                }
                                if (!sidebarCollapsed) {
                                    Span({ classes(DashboardStyles.categoryText) }) { Text(title) }
                                }
                            }

                            if (isOpen && !sidebarCollapsed) {
                                screens.forEach { screen ->
                                    val active = screen.key == selectedKey
                                    Div({
                                        classes(DashboardStyles.screenRow)
                                        if (active) classes(DashboardStyles.screenActiveRow)
                                        onClick {
                                            onSelect(screen.key)
                                            onPin(screen.key)
                                        }
                                    }) {
                                        Img("images/step.svg") { classes(DashboardStyles.stepIcon) }
                                        Span { Text(screen.label) }
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                Div({ classes(DashboardStyles.navScroll) }) {
                    Div({ classes(DashboardStyles.navScrollContent) }) {
                        sidebarScreens.forEach { screen ->
                            val active = screen.key == selectedKey
                            Div({
                                classes(DashboardStyles.screenRow)
                                if (sidebarCollapsed) classes(DashboardStyles.screenRowCollapsed)
                                if (active) classes(DashboardStyles.screenActiveRow)
                                if (!sidebarCollapsed) {
                                    style {
                                        padding(10.px, 18.px, 10.px, 24.px)
                                        gap(12.px)
                                    }
                                }
                                onMouseEnter {
                                    if (sidebarCollapsed) {
                                        val event = it.nativeEvent as? MouseEvent
                                        hoverFlatTopPx = event?.currentTarget.asDynamic().getBoundingClientRect().top as? Double
                                            ?: event?.clientY?.toDouble()
                                            ?: 0.0
                                        hoverFlatScreen = screen
                                        hoverCategory = null
                                    }
                                }
                                onClick {
                                    onSelect(screen.key)
                                    onPin(screen.key)
                                }
                            }) {
                                Img(screen.icon.ifBlank { "images/step.svg" }) {
                                    classes(DashboardStyles.stepIcon)
                                    if (!sidebarCollapsed) style { marginRight(12.px) }
                                }
                                if (!sidebarCollapsed) {
                                    Span({ style { fontSize(14.px) } }) { Text(screen.label) }
                                }
                            }
                        }
                    }
                }
            }

            Div({ classes(DashboardStyles.sidebarIdentityBlock) }) {
                DashboardSidebarIdentityRow(
                    icon = "images/unternehmen.svg",
                    label = activeCompanyLabel,
                    collapsed = sidebarCollapsed,
                    onHover = { label, top ->
                        hoverIdentityLabel = label
                        hoverIdentityTopPx = top
                    },
                    onClick = onOpenCompany
                )
                DashboardSidebarIdentityRow(
                    icon = "images/user.svg",
                    label = currentUser,
                    collapsed = sidebarCollapsed,
                    onHover = { label, top ->
                        hoverIdentityLabel = label
                        hoverIdentityTopPx = top
                    },
                    onClick = { onOpenProfilePopup?.invoke() ?: onOpenProfile?.invoke() }
                )
                DashboardSidebarIdentityRow(
                    icon = if (sidebarCollapsed) "images/left_panel_open.svg" else "images/left_panel_close.svg",
                    label = if (sidebarCollapsed) "Maximieren" else "Minimieren",
                    collapsed = sidebarCollapsed,
                    onHover = { label, top ->
                        hoverIdentityLabel = label
                        hoverIdentityTopPx = top
                    },
                    onClick = { onSidebarCollapsedChange(!sidebarCollapsed) }
                )
            }
        }

        if (sidebarCollapsed) {
            val popupCategory = hoverCategory
            if (popupCategory != null) {
                DashboardSidebarCategoryPopup(
                    title = popupCategory,
                    screens = categories[popupCategory].orEmpty(),
                    selectedKey = selectedKey,
                    leftPx = sidebarWidthPx,
                    topPx = hoverCategoryTopPx,
                    widthPx = expandedSidebarWidthPx,
                    onSelect = { key ->
                        onSelect(key)
                        onPin(key)
                    }
                )
            }
            val popupScreen = hoverFlatScreen
            if (popupScreen != null) {
                DashboardSidebarFlatPopup(
                    screen = popupScreen,
                    selectedKey = selectedKey,
                    leftPx = sidebarWidthPx,
                    topPx = hoverFlatTopPx,
                    widthPx = expandedSidebarWidthPx,
                    onSelect = { key ->
                        onSelect(key)
                        onPin(key)
                    }
                )
            }
            if (popupGridVisible && popupScreens.isNotEmpty()) {
                DashboardSidebarPopupGrid(
                    screens = popupScreens,
                    openTopbarScreens = openTopbarScreens,
                    leftPx = sidebarWidthPx,
                    topPx = popupGridTopPx,
                    widthPx = expandedSidebarWidthPx,
                    onHover = { label, event -> showPopupTooltip(event, label) },
                    onHoverExit = { hoverPopupLabel = null },
                    onToggleTopbarScreen = onToggleTopbarScreen
                )
            }
            val identityLabel = hoverIdentityLabel
            if (identityLabel != null) {
                DashboardSidebarTextPopup(
                    label = identityLabel,
                    leftPx = sidebarWidthPx,
                    topPx = hoverIdentityTopPx
                )
            }
            val popupLabel = hoverPopupLabel
            if (popupLabel != null) {
                DashboardSidebarMouseTooltip(
                    label = popupLabel,
                    leftPx = hoverPopupLeftPx,
                    topPx = hoverPopupTopPx
                )
            }
        } else {
            val popupLabel = hoverPopupLabel
            if (popupLabel != null) {
                DashboardSidebarMouseTooltip(
                    label = popupLabel,
                    leftPx = hoverPopupLeftPx,
                    topPx = hoverPopupTopPx
                )
            }
        }
    }
}

@Composable
private fun DashboardSidebarIdentityRow(
    icon: String,
    label: String,
    collapsed: Boolean,
    onHover: (String, Double) -> Unit,
    onClick: (() -> Unit)? = null
) {
    Div({
        classes(DashboardStyles.sidebarIdentityRow)
        if (collapsed) classes(DashboardStyles.sidebarIdentityRowCollapsed)
        if (collapsed) {
            onMouseEnter {
                val event = it.nativeEvent as? MouseEvent
                val top = event?.currentTarget.asDynamic().getBoundingClientRect().top as? Double
                    ?: event?.clientY?.toDouble()
                    ?: 0.0
                onHover(label, top)
            }
        }
        if (onClick != null) onClick { onClick() }
    }) {
        Img(icon) { classes(DashboardStyles.sidebarIdentityIcon) }
        if (!collapsed) {
            Span({ classes(DashboardStyles.sidebarIdentityText) }) { Text(label) }
        }
    }
}

@Composable
private fun DashboardSidebarTextPopup(
    label: String,
    leftPx: Int,
    topPx: Double
) {
    Div({
        classes(DashboardStyles.sidebarTextHoverPopup)
        style {
            left(leftPx.px)
            top(topPx.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun DashboardSidebarMouseTooltip(
    label: String,
    leftPx: Double,
    topPx: Double
) {
    Div({
        classes(DashboardStyles.sidebarTextHoverPopup)
        style {
            left(leftPx.px)
            top(topPx.px)
            property("pointer-events", "none")
        }
    }) {
        Text(label)
    }
}

@Composable
private fun DashboardSidebarPopupGrid(
    screens: List<All>,
    openTopbarScreens: List<String>,
    leftPx: Int,
    topPx: Double,
    widthPx: Int,
    onHover: (String, MouseEvent?) -> Unit,
    onHoverExit: () -> Unit,
    onToggleTopbarScreen: (String, Boolean) -> Unit
) {
    Div({
        classes(DashboardStyles.sidebarPopupGrid)
        style {
            left(leftPx.px)
            top(topPx.px)
            width(widthPx.px)
        }
    }) {
        screens.forEach { screen ->
            val active = screen.key in openTopbarScreens
            Div({
                classes(DashboardStyles.sidebarPopupGridItem)
                if (active) classes(DashboardStyles.sidebarPopupIconButtonActive)
                onMouseEnter {
                    onHover(screen.label.ifBlank { screen.key }, it.nativeEvent as? MouseEvent)
                }
                onMouseMove {
                    onHover(screen.label.ifBlank { screen.key }, it.nativeEvent as? MouseEvent)
                }
                onMouseLeave {
                    onHoverExit()
                }
                onClick { onToggleTopbarScreen(screen.key, active) }
            }) {
                Img(screen.icon.ifBlank { "images/step.svg" }) {
                    classes(DashboardStyles.sidebarPopupIcon)
                }
            }
        }
    }
}

@Composable
private fun DashboardSidebarCategoryPopup(
    title: String,
    screens: List<All>,
    selectedKey: String,
    leftPx: Int,
    topPx: Double,
    widthPx: Int,
    onSelect: (String) -> Unit
) {
    Div({
        classes(DashboardStyles.sidebarHoverPopup)
        style {
            left(leftPx.px)
            top(topPx.px)
            width(widthPx.px)
        }
    }) {
        Div({ classes(DashboardStyles.sidebarHoverPopupTitle) }) { Text(title) }
        screens.forEach { screen ->
            val active = screen.key == selectedKey
            Div({
                classes(DashboardStyles.screenRow)
                if (active) classes(DashboardStyles.screenActiveRow)
                onClick { onSelect(screen.key) }
            }) {
                Img("images/step.svg") { classes(DashboardStyles.stepIcon) }
                Span { Text(screen.label) }
            }
        }
    }
}

@Composable
private fun DashboardSidebarFlatPopup(
    screen: All,
    selectedKey: String,
    leftPx: Int,
    topPx: Double,
    widthPx: Int,
    onSelect: (String) -> Unit
) {
    Div({
        classes(DashboardStyles.sidebarHoverPopup)
        style {
            left(leftPx.px)
            top(topPx.px)
            width(widthPx.px)
        }
    }) {
        val active = screen.key == selectedKey
        Div({
            classes(DashboardStyles.screenRow)
            if (active) classes(DashboardStyles.screenActiveRow)
            onClick { onSelect(screen.key) }
        }) {
            Img(screen.icon.ifBlank { "images/step.svg" }) { classes(DashboardStyles.stepIcon) }
            Span { Text(screen.label) }
        }
    }
}

@Composable
internal fun DashboardUserSettingsPopup(
    feedDefinitionsWithStatus: List<Pair<DashboardFeedDefinition, Boolean>>,
    userFeedPushEnabled: Map<String, Boolean>,
    onFeedPushEnabledChange: (String, Boolean) -> Unit,
    onFeedSettingsSave: ((Map<String, Boolean>) -> Unit)? = null,
    startScreenOptions: List<All> = emptyList(),
    selectedStartScreen: String = "",
    onStartScreenSave: ((String) -> Unit)? = null,
    onClose: () -> Unit
) {
    val allowedFeeds = feedDefinitionsWithStatus.filter { (_, active) -> active }
    var draftFeedSettings by remember(userFeedPushEnabled, allowedFeeds) {
        mutableStateOf(
            allowedFeeds.associate { (feed, _) ->
                feed.key to (userFeedPushEnabled[feed.key] != false)
            }
        )
    }
    var draftStartScreen by remember(selectedStartScreen) { mutableStateOf(selectedStartScreen) }

    PopupEngineCard(
        windowKey = "dashboardUserFeedSettings",
        widthPx = 420,
        heightPx = 440,
        minWidthPx = 360,
        minHeightPx = 320,
        initialX = (window.innerWidth.toDouble() - 480.0).coerceAtLeast(84.0),
        initialY = 96.0,
        contentBackground = DashboardTheme.POPUP_BG,
        frameBackground = null,
        resizable = true,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({
            style {
                height(100.percent)
                width(100.percent)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.FlexStart)
                boxSizing("border-box")
                padding(34.px, 30.px, 26.px, 30.px)
            }
        }) {
            Div({
                style {
                    width(280.px)
                    maxHeight(100.percent)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Stretch)
                    gap(8.px)
                    minHeight(0.px)
                }
            }) {
                Div({
                    classes(DashboardStyles.userSettingsTitleRow)
                    style { justifyContent(JustifyContent.Center) }
                }) {
                    Span({ classes(DashboardStyles.userSettingsTitle) }) { Text("Dashboard Einstellungen") }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(8.px)
                        flex(1)
                        minHeight(0.px)
                        overflowY("auto")
                    }
                }) {
                    if (startScreenOptions.isNotEmpty() && onStartScreenSave != null) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(8.px)
                                padding(12.px, 4.px)
                            }
                        }) {
                            Span({ classes(DashboardStyles.userSettingsLabel) }) { Text("Startbildschirm") }
                            AppDropdown(
                                options = startScreenOptions,
                                selectedValue = draftStartScreen.ifBlank { null },
                                onChange = { draftStartScreen = it },
                                placeholder = "Startbildschirm wählen",
                                appearance = AppDropdownAppearance.Underline,
                                attrs = {
                                    style {
                                        width(100.percent)
                                        minHeight(36.px)
                                    }
                                }
                            )
                        }
                        Div({ classes(DashboardStyles.userSettingsDivider) })
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                            padding(12.px, 4.px)
                        }
                    }) {
                        allowedFeeds.forEach { (feed, _) ->
                            DashboardFeedPushRow(
                                icon = feed.icon,
                                label = feed.label,
                                enabled = draftFeedSettings[feed.key] != false,
                                onToggle = { enabled ->
                                    draftFeedSettings = draftFeedSettings + (feed.key to enabled)
                                }
                            )
                        }
                    }
                }

                Div({ classes(DashboardStyles.userSettingsDivider) })

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(8.px)
                        padding(12.px, 4.px)
                    }
                }) {
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            if (onFeedSettingsSave != null) {
                                onFeedSettingsSave(draftFeedSettings)
                            } else {
                                draftFeedSettings.forEach { (feedKey, enabled) ->
                                    onFeedPushEnabledChange(feedKey, enabled)
                                }
                            }
                            onStartScreenSave?.invoke(draftStartScreen)
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 36
                    )
                }

            }
        }
    }
}

@Composable
internal fun DashboardUserActionsPopup(
    onPasswordChange: (() -> Unit)?,
    onLogout: () -> Unit,
    onClose: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val trustedDevicesApi = "desktopregistrierungscreen"
    var showDevices by remember { mutableStateOf(false) }
    var devices by remember { mutableStateOf<List<TrustedDevice>>(emptyList()) }
    var loadingDevices by remember { mutableStateOf(false) }
    var selectedTrustedDeviceIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var editingTrustedDeviceId by remember { mutableStateOf("") }
    var editingTrustedDeviceLabel by remember { mutableStateOf("") }

    fun reloadDevices() {
        loadingDevices = true
        scope.launch {
            devices = TrustedDevicesApi.load(trustedDevicesApi)
            val ids = devices.map { it.id }.toSet()
            selectedTrustedDeviceIds = selectedTrustedDeviceIds.filter { it in ids }.toSet()
            if (editingTrustedDeviceId.isNotBlank() && editingTrustedDeviceId !in ids) editingTrustedDeviceId = ""
            loadingDevices = false
        }
    }

    PopupEngineCard(
        windowKey = "dashboardUserActions",
        widthPx = if (showDevices) 460 else 340,
        heightPx = if (showDevices) 430 else 260,
        minWidthPx = if (showDevices) 420 else 300,
        minHeightPx = 220,
        initialX = (window.innerWidth.toDouble() - 400.0).coerceAtLeast(84.0),
        initialY = 120.0,
        contentBackground = DashboardTheme.POPUP_BG,
        frameBackground = null,
        resizable = true,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({
            style {
                height(100.percent)
                width(100.percent)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                boxSizing("border-box")
                padding(30.px)
            }
        }) {
            Div({
                style {
                    width(if (showDevices) 380.px else 240.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Stretch)
                    gap(8.px)
                }
            }) {
                Div({
                    classes(DashboardStyles.userSettingsTitleRow)
                    style { justifyContent(JustifyContent.Center) }
                }) {
                    Span({ classes(DashboardStyles.userSettingsTitle) }) {
                        Text(if (showDevices) "2F Browser-Freigaben" else "Benutzerkonto")
                    }
                }

                Div({ classes(DashboardStyles.userSettingsDivider) })

                if (showDevices) {
                    DashboardTrustedDevicesPanel(
                        devices = devices,
                        selectedDeviceIds = selectedTrustedDeviceIds,
                        loading = loadingDevices,
                        onSelect = { device ->
                            selectedTrustedDeviceIds = if (device.id in selectedTrustedDeviceIds) {
                                selectedTrustedDeviceIds - device.id
                            } else {
                                selectedTrustedDeviceIds + device.id
                            }
                        },
                        editingDeviceId = editingTrustedDeviceId,
                        editingLabel = editingTrustedDeviceLabel,
                        onEdit = { device ->
                            editingTrustedDeviceId = device.id
                            editingTrustedDeviceLabel = device.label.ifBlank { trustedDeviceFallbackLabel(device) }
                        },
                        onEditLabel = { editingTrustedDeviceLabel = it },
                        onSaveLabel = {
                            val id = editingTrustedDeviceId
                            val label = editingTrustedDeviceLabel.trim()
                            if (id.isBlank() || label.isBlank()) return@DashboardTrustedDevicesPanel
                            loadingDevices = true
                            scope.launch {
                                if (TrustedDevicesApi.rename(trustedDevicesApi, id, label)) {
                                    devices = TrustedDevicesApi.load(trustedDevicesApi)
                                    editingTrustedDeviceId = ""
                                    editingTrustedDeviceLabel = ""
                                }
                                loadingDevices = false
                            }
                        },
                        onCancelEdit = {
                            editingTrustedDeviceId = ""
                            editingTrustedDeviceLabel = ""
                        },
                        onCancel = {
                            selectedTrustedDeviceIds = emptySet()
                            editingTrustedDeviceId = ""
                            editingTrustedDeviceLabel = ""
                            showDevices = false
                        },
                        onDeleteSelected = {
                            val ids = selectedTrustedDeviceIds
                            if (ids.isEmpty()) return@DashboardTrustedDevicesPanel
                            loadingDevices = true
                            scope.launch {
                                ids.forEach { TrustedDevicesApi.delete(trustedDevicesApi, it) }
                                devices = TrustedDevicesApi.load(trustedDevicesApi)
                                selectedTrustedDeviceIds = emptySet()
                                loadingDevices = false
                            }
                        }
                    )
                } else {
                    DashboardSettingsIconAction(
                        icon = "images/identity.svg",
                        label = "2F Browser-Freigaben",
                        onClick = {
                            showDevices = true
                            selectedTrustedDeviceIds = emptySet()
                            editingTrustedDeviceId = ""
                            editingTrustedDeviceLabel = ""
                            reloadDevices()
                        }
                    )

                    if (onPasswordChange != null) {
                        DashboardSettingsIconAction(
                            icon = "images/lock_reset.svg",
                            label = "Passwort ändern",
                            onClick = onPasswordChange
                        )
                    }

                    DashboardSettingsIconAction(
                        icon = "images/logout.svg",
                        label = "Logout",
                        onClick = onLogout
                    )
                }
            }
        }
    }
}

@Composable
internal fun DashboardTrustedDevicesPanel(
    devices: List<TrustedDevice>,
    selectedDeviceIds: Set<String>,
    loading: Boolean,
    onSelect: (TrustedDevice) -> Unit,
    editingDeviceId: String,
    editingLabel: String,
    onEdit: (TrustedDevice) -> Unit,
    onEditLabel: (String) -> Unit,
    onSaveLabel: () -> Unit,
    onCancelEdit: () -> Unit,
    onCancel: () -> Unit,
    onDeleteSelected: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
        }
    }) {
        if (devices.isEmpty()) {
            Span({ classes(DashboardStyles.userSettingsLabel) }) {
                Text(if (loading) "Lade..." else "Keine Geräte hinterlegt")
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                    maxHeight(230.px)
                    overflowY("auto")
                }
            }) {
                devices.forEach { device ->
                    TrustedDeviceSelectionRow(
                        device = device,
                        selected = device.id in selectedDeviceIds,
                        enabled = !loading,
                        editing = editingDeviceId == device.id,
                        editingLabel = editingLabel,
                        onSelect = { onSelect(device) },
                        onEdit = { onEdit(device) },
                        onEditLabel = onEditLabel,
                        onSaveLabel = onSaveLabel,
                        onCancelEdit = onCancelEdit
                    )
                }
            }
        }

        Div({ classes(DashboardStyles.userSettingsDivider) })

        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
                gap(8.px)
            }
        }) {
            LumaticaButton(
                label = "Abbrechen",
                onClick = onCancel,
                variant = LumaticaButtonVariant.Standard,
                enabled = !loading,
                heightPx = 34
            )
            LumaticaButton(
                label = if (loading) "..." else "Auswahl löschen",
                onClick = onDeleteSelected,
                variant = LumaticaButtonVariant.Action,
                enabled = !loading && selectedDeviceIds.isNotEmpty(),
                heightPx = 34
            )
        }
    }
}

@Composable
private fun TrustedDeviceSelectionRow(
    device: TrustedDevice,
    selected: Boolean,
    enabled: Boolean,
    editing: Boolean,
    editingLabel: String,
    onSelect: () -> Unit,
    onEdit: () -> Unit,
    onEditLabel: (String) -> Unit,
    onSaveLabel: () -> Unit,
    onCancelEdit: () -> Unit
) {
    Div({
        classes(DashboardStyles.userSettingsRow)
        style {
            gap(10.px)
            padding(8.px, 6.px)
            alignItems(AlignItems.FlexStart)
            cursor(if (enabled) "pointer" else "default")
        }
        if (enabled) onClick { onSelect() }
    }) {
        LumaticaCheckbox(
            checked = selected,
            onChange = { onSelect() },
            enabled = enabled,
            fontSizePx = 18
        )
        Div({
            style {
                flex(1)
                minWidth(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(3.px)
            }
        }) {
            if (editing) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(6.px)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Input(InputType.Text, attrs = {
                        value(editingLabel)
                        onInput { onEditLabel(it.value) }
                        style {
                            flex(1)
                            minWidth(0.px)
                            height(30.px)
                            borderRadius(6.px)
                            property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                            backgroundColor(Color("rgba(255,255,255,0.08)"))
                            color(DashboardTheme.TEXT_LIGHT)
                            padding(0.px, 8.px)
                        }
                    })
                    LumaticaButton(label = "OK", onClick = onSaveLabel, variant = LumaticaButtonVariant.Action, enabled = enabled && editingLabel.isNotBlank(), heightPx = 30)
                    LumaticaButton(label = "X", onClick = onCancelEdit, variant = LumaticaButtonVariant.Standard, enabled = enabled, heightPx = 30)
                }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Span({
                        classes(DashboardStyles.userSettingsLabel)
                        style {
                            flex(1)
                            minWidth(0.px)
                            property("overflow-wrap", "anywhere")
                            lineHeight("1.25")
                        }
                    }) {
                        Text(device.label.ifBlank { trustedDeviceFallbackLabel(device) } + if (device.current) " (dieser Browser)" else "")
                    }
                    LumaticaButton(label = "Umbenennen", onClick = onEdit, variant = LumaticaButtonVariant.Standard, enabled = enabled, heightPx = 30)
                }
            }
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(11.px)
                    lineHeight("1.35")
                    property("overflow-wrap", "anywhere")
                }
            }) {
                Text(trustedDeviceDetails(device))
            }
        }
    }
}

internal fun trustedDeviceFallbackLabel(device: TrustedDevice): String =
    when (device.mode.lowercase()) {
        "mobile" -> "Fahrer-App"
        "desktop" -> "Desktop-Browser"
        else -> "Browser-Freigabe"
    }

internal fun trustedDeviceDetails(device: TrustedDevice): String {
    val parts = listOf(
        device.clientIp.takeIf { it.isNotBlank() },
        device.userAgent.takeIf { it.isNotBlank() }
    ).filterNotNull()
    return parts.joinToString(" | ").ifBlank { "Gespeicherte 2F-Browser-Freigabe" }
}

@Composable
private fun DashboardFeedPushRow(
    icon: String,
    label: String,
    enabled: Boolean,
    onToggle: (Boolean) -> Unit
) {
    Div({
        classes(DashboardStyles.userSettingsRow)
        style {
            gap(12.px)
            padding(9.px, 4.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                minWidth(0.px)
            }
        }) {
            Img(icon) {
                classes(DashboardStyles.userActionIcon)
                style {
                    property("filter", DashboardTheme.ICON_FILTER_WHITE)
                    property("opacity", "1")
                }
            }
            Span({ classes(DashboardStyles.userSettingsLabel) }) { Text(label) }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                minWidth(44.px)
            }
        }) {
            LumaticaCheckbox(
                checked = enabled,
                onChange = { onToggle(!enabled) }
            )
        }
    }
}

@Composable
private fun DashboardSettingsIconAction(
    icon: String,
    label: String,
    onClick: () -> Unit,
    active: Boolean = false
) {
    Div({
        classes(DashboardStyles.userSettingsRow)
        onClick { onClick() }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            Img(icon) {
                classes(DashboardStyles.userActionIcon)
                style {
                    property("filter", if (active) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE)
                }
            }
            Span({ classes(DashboardStyles.userSettingsLabel) }) { Text(label) }
        }
    }
}
